import {
  Button,
  ButtonSize,
  ButtonVariant,
  Show,
  Hide
} from "shared/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";

import { Clock, Settings04 } from "untitledui-js-base";
import moment from "moment";
import {
  Applicant,
  Application,
  MakeDecisionRequirements
} from "types/application";
import { ApplicationStatusBanner } from "../ApplicationStatusBanner";
import { ApplicationStatus, Invitee } from "shared/types";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { cx, formatPhone } from "shared/utils";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { usePopoverActions } from "views/applications-id/components/LayoutHeader/hooks/usePopoverActions";
import {
  canDecisionBeMade,
  canSeeDecisionButton,
  getUnableToMakeDecisionReason
} from "utils/canMakeDecision";
import { UndoDecisionModal } from "./components/UndoDecisionModal";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";

import { getInviteApplicationDisabledToolTip } from "./helpers";
import { resumeApplication } from "shared/app/OnHoldResume/api";

interface ILayoutHeader {
  applicationId: string;
  applicants?: Applicant[];
  application: Application;
  invitees: Invitee[];
  disableDecisionFlow?: boolean;
  applicationStatus: ApplicationStatus;
  decisionAt: string | null;
  submittedAt: string | null;
  setShowActivityLog: (value: boolean) => void;
  setShowDecisionFlowModal?: (isOpen: boolean) => void;
  setDecisionConfirmationModal?: (isOpen: boolean) => void;
  setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  viewTemplate: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onMoveClick?: () => void;
  onSplitClick?: () => void;
  onRefundClick?: () => void;
  requirements?: MakeDecisionRequirements[];
  hideApplicantNamesAndUnitInfo?: boolean;
}

export const LayoutHeader = ({
  applicationId,
  applicants,
  application,
  applicationStatus,
  decisionAt,
  viewTemplate,
  setShowActivityLog,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  setShowInviteApplicantsModal,
  onCancelClick,
  onUncancelClick,
  onSplitClick,
  onMoveClick,
  onRefundClick,
  requirements,
  hideApplicantNamesAndUnitInfo
}: ILayoutHeader) => {
  const { permissions, type } = useAuthContext();
  const { adminPrivileges } = type;
  const actions = usePopoverActions({
    applicationId,
    applicants,
    application,
    onMoveClick,
    onRefundClick,
    onCancelClick,
    onUncancelClick,
    onSplitClick,
    applicationStatus
  });

  const [undoDecisionModalOpened, setUndoDecisionModalOpened] = useState(false);
  const [notifyApplicant, setNotifyApplicant] = useState(false);

  const queryClient = useQueryClient();

  const { mutate: undoDecision, isPending: undoDecisionPending } = useMutation({
    mutationFn: () => {
      return instance.post(
        `portal/v1/applications/${applicationId}/undo_decision`,
        {
          notify_applicant: notifyApplicant
        }
      );
    },
    onSuccess: () => {
      toast.success("Application successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });
      setUndoDecisionModalOpened(false);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const canMakeDecision = canDecisionBeMade({
    applicants: applicants || [],
    status: applicationStatus,
    type,
    permissions,
    requirements
  });
  const cannotMakeDecisionText = !canMakeDecision
    ? getUnableToMakeDecisionReason({
        requirements
      })
    : undefined;

  const isMakeDecisionVisible = canSeeDecisionButton({
    permissions,
    type,
    status: applicationStatus
  });

  return (
    <div>
      <div className="flex items-center justify-between border-b border-alpha-40 px-3 lg:px-6 py-3 lg:flex-nowrap flex-wrap">
        <div className="flex items-center gap-2.5 lg:h-auto h-5">
          <BreadCrumbs
            crumbs={[
              {
                title: "Applications",
                href: "/applications"
              },
              {
                title:
                  applicants
                    ?.map((app) =>
                      app.first_name && app.last_name
                        ? getFullName({
                            firstName: app.first_name,
                            lastName: app.last_name,
                            middleName: app.middle_name,
                            short: true
                          })
                        : app.email
                          ? app.email
                          : formatPhone(app.phone)
                    )
                    .join(" • ") || "Application with invitees",
                href: `/applications/${applicationId}`
              }
            ]}
          />
        </div>
        <div className="flex">
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            Icon={Settings04}
            className="hidden lg:flex !text-content-400 !stroke-content-400"
            onClick={() => {
              viewTemplate();
            }}
          >
            Screening settings
          </Button>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="hidden lg:flex !text-content-400 !stroke-content-400"
            Icon={Clock}
            onClick={() => setShowActivityLog(true)}
          >
            Activity log
          </Button>
        </div>
      </div>
      <ApplicationStatusBanner status={applicationStatus} />

      <div
        className={cx(
          "flex items-center justify-between p-3 lg:p-6 flex-wrap lg:flex-nowrap transition-all",
          hideApplicantNamesAndUnitInfo ? "h-[72px]" : "h-[108px]"
        )}
      >
        <div>
          <ApplicationStatusBadge
            type="application"
            status={applicationStatus}
          />

          <Hide if={hideApplicantNamesAndUnitInfo}>
            <h4 className="text-xl font-bold text-content-1000 flex flex-wrap items-center gap-4 mt-2 animate-bounce-in">
              {applicants?.map((app, index) => (
                <span
                  key={app.id || index}
                  className="flex items-center border-b border-transparent gap-4"
                >
                  {getFullName({
                    firstName: app.first_name,
                    lastName: app.last_name,
                    middleName: app.middle_name,
                    short: true
                  })}
                  <Show if={index !== applicants.length - 1}>
                    <span className="size-1.5 rounded-full bg-alpha-100" />
                  </Show>
                </span>
              ))}
            </h4>
          </Hide>
        </div>
        <div className="flex items-center justify-end gap-2">
          {adminPrivileges || permissions.canCancelApplications ? (
            <ContextMenuPopover borderLess={false} actions={actions} />
          ) : null}

          {[
            ApplicationStatus.Approved,
            ApplicationStatus.Rejected,
            ApplicationStatus.ConditionalApproved
          ].includes(applicationStatus) && permissions.canMakeDecisions ? (
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => setUndoDecisionModalOpened(true)}
              disabled={
                !decisionAt ||
                moment(decisionAt).isBefore(moment().subtract(30, "days"))
              }
              tooltip={
                !decisionAt ||
                moment(decisionAt).isBefore(moment().subtract(30, "days"))
                  ? "Undoing decisions is allowed for 30 days after a decision is made"
                  : undefined
              }
              tooltipZIndex={21}
            >
              Undo decision
            </Button>
          ) : (
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => setShowInviteApplicantsModal?.(true)}
              disabled={
                ![
                  ApplicationStatus.Pending,
                  ApplicationStatus.Submitted,
                  ApplicationStatus.Started
                ].includes(applicationStatus)
              }
              className="hidden md:flex"
              tooltip={getInviteApplicationDisabledToolTip(applicationStatus)}
              tooltipZIndex={21}
            >
              Invite applicant
            </Button>
          )}
          <Show if={isMakeDecisionVisible}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                applicationStatus === ApplicationStatus.Submitted
                  ? setShowDecisionFlowModal?.(true)
                  : setDecisionConfirmationModal?.(true);
              }}
              className="hidden md:flex"
              disabled={!canMakeDecision}
              tooltip={cannotMakeDecisionText}
              tooltipPlacement="bottom-end"
              tooltipZIndex={2}
            >
              Make decision
            </Button>
          </Show>

          <Show if={applicationStatus === ApplicationStatus.OnHold}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                resumeApplication({
                  applicationId,
                  onSuccess: () => {
                    queryClient.refetchQueries({
                      queryKey: ["APPLICATION", applicationId]
                    });
                  }
                });
              }}
              className="hidden md:flex"
            >
              Resume
            </Button>
          </Show>
        </div>
      </div>

      {undoDecisionModalOpened ? (
        <UndoDecisionModal
          opened={undoDecisionModalOpened}
          onClose={() => {
            setUndoDecisionModalOpened(false);
          }}
          undoDecisionPending={undoDecisionPending}
          onUndo={() => undoDecision()}
          setNotifyApplicant={setNotifyApplicant}
          notifyApplicant={notifyApplicant}
        />
      ) : null}
    </div>
  );
};
